import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { isOverdue } from "../../../utils/activityHelpers";
import ActivityLink from "../../activity-link/ActivityLink";
import VisualActionDialog from "../../next-action/VisualActionDialog";
import WorkItemListItem from "../../workitem/WorkItemListItem";
import { WorkItemList, WorkItemStateEnum } from "../../../store/types/work.types";

interface ActivityStreamCardProps {
  title: string;
  workItems: WorkItemList[];
  customerNo: number;
  workId?: number; // Optional workId for Work context
  showNewActivityButton?: boolean;
  isLoading?: boolean;
  error?: unknown;
}

const ITEMS_PER_PAGE = 5;

const ActivityStreamHeader = ({
  title,
  totalCount,
  customerNo,
  workId,
  page,
  onPageChange,
  activeCount,
  expanded,
  onToggleExpanded,
  showNewActivityButton,
}: {
  title: string;
  totalCount: number;
  customerNo: number;
  workId?: number;
  page: number;
  onPageChange: (page: number) => void;
  activeCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  showNewActivityButton?: boolean;
}) => {
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: expanded ? 3 : 1,
          cursor: "pointer",
        }}
        onClick={onToggleExpanded}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">{title}</Typography>

          <Chip
            label={totalCount}
            size="small"
            color="primary"
            variant="outlined"
          />

          {activeCount > 0 && (
            <Chip
              label={`${activeCount} aktiva`}
              size="small"
              color="warning"
              variant="filled"
            />
          )}

          {showNewActivityButton && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setVisualDialogOpen(true);
              }}
            >
              Ny aktivitet
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {expanded && totalCount > ITEMS_PER_PAGE && (
            <>
              <Typography variant="body2" color="text.secondary">
                Sida {page} av {Math.ceil(totalCount / ITEMS_PER_PAGE)}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  disabled={page === 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPageChange(page - 1);
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={page >= Math.ceil(totalCount / ITEMS_PER_PAGE)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPageChange(page + 1);
                  }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </Box>
            </>
          )}

          <IconButton
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      <VisualActionDialog
        open={visualDialogOpen}
        customerNo={customerNo}
        workId={workId}
        onClose={() => setVisualDialogOpen(false)}
      />
    </>
  );
};

// Minimized preview component using WorkItemListItem
const ActivityPreview = ({ workItems }: { workItems: WorkItemList[] }) => {
  const recentItems = workItems.slice(0, 3);

  return (
    <Box sx={{ py: 1, display: "flex", flexDirection: "column", gap: 1 }}>
      {recentItems.map((item) => (
        <WorkItemListItem
          resultData={item.Result}
          key={item.Id}
          workItemType={item.Type}
          title={item.Name}
          description={item.Description}
          date={item.ScheduledTo}
          customerName={item.Customer?.Name || ""}
          workId={item.WorkId}
          showAction={false}
          isCompleted={item.State === WorkItemStateEnum.Closed}
          isActive={item.State === WorkItemStateEnum.Active}
          isOverdue={isOverdue(item)}
          compact
        />
      ))}

      {workItems.length > 3 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: 1, fontStyle: "italic" }}
        >
          ...och {workItems.length - 3} till
        </Typography>
      )}
    </Box>
  );
};

const ActivityStreamCard = ({
  title,
  workItems,
  customerNo,
  workId,
  showNewActivityButton = true,
}: ActivityStreamCardProps) => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(false);

  // Sort work items: Overdue first, then active by nearest date, then completed by changed date
  const sortedWorkItems = [...workItems].sort((a, b) => {
    const aIsOverdue = isOverdue(a);
    const bIsOverdue = isOverdue(b);
    const aIsActive = a.State === WorkItemStateEnum.Active;
    const bIsActive = b.State === WorkItemStateEnum.Active;
    const aIsCompleted = a.State === WorkItemStateEnum.Closed;
    const bIsCompleted = b.State === WorkItemStateEnum.Closed;

    // 1. Overdue items first (regardless of state)
    if (aIsOverdue !== bIsOverdue) {
      return aIsOverdue ? -1 : 1;
    }

    // 2. If both overdue or both not overdue, sort by state priority
    if (aIsActive !== bIsActive) {
      return aIsActive ? -1 : 1; // Active before completed
    }

    // 3. Within same category, sort appropriately
    if (aIsActive && bIsActive) {
      // For active items: nearest scheduled date first
      const aScheduled = new Date(a.ScheduledTo).getTime();
      const bScheduled = new Date(b.ScheduledTo).getTime();
      return aScheduled - bScheduled;
    }

    if (aIsCompleted && bIsCompleted) {
      // For completed items: most recently changed first
      const aChanged = a.Modified
        ? new Date(a.Modified).getTime()
        : new Date(a.Created).getTime();
      const bChanged = b.Modified
        ? new Date(b.Modified).getTime()
        : new Date(b.Created).getTime();
      return bChanged - aChanged;
    }

    // Fallback: sort by scheduled date
    const aScheduled = new Date(a.ScheduledTo).getTime();
    const bScheduled = new Date(b.ScheduledTo).getTime();
    return aScheduled - bScheduled;
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setPage(1); // Reset to first page when expanding
    }
  };

  const paginatedActivities = sortedWorkItems.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Count active work items
  const activeCount = workItems.filter(
    (item) => item.State === WorkItemStateEnum.Active
  ).length;

  return (
    <Card
      sx={{
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: expanded ? 2 : 1,
        },
      }}
    >
      <CardContent sx={{ "&:last-child": { pb: expanded ? 2 : 1 } }}>
        <ActivityStreamHeader
          title={title}
          customerNo={customerNo}
          workId={workId}
          totalCount={sortedWorkItems.length}
          page={page}
          onPageChange={handlePageChange}
          activeCount={activeCount}
          expanded={expanded}
          onToggleExpanded={toggleExpanded}
          showNewActivityButton={showNewActivityButton}
        />

        {/* Minimized preview */}
        {!expanded && sortedWorkItems.length > 0 && (
          <ActivityPreview workItems={sortedWorkItems} />
        )}

        {/* Full activity list */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(0,0,0,0.3)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {paginatedActivities.map((activity, index) => (
                <ActivityLink
                  key={activity.Id || index}
                  to={`/activity/${activity.Id}`}
                  customerNo={customerNo}
                  workItemNo={activity.Id}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <WorkItemListItem
                    resultData={activity.Result}
                    workItemType={activity.Type}
                    title={activity.Name}
                    description={activity.Description}
                    date={activity.ScheduledTo}
                    customerName={activity.Customer?.Name || ""}
                    workId={activity.WorkId}
                    showAction={false}
                    isCompleted={activity.State === WorkItemStateEnum.Closed}
                    isActive={activity.State === WorkItemStateEnum.Active}
                    isOverdue={isOverdue(activity)}
                  />
                </ActivityLink>
              ))}
            </Box>
          </Box>
        </Collapse>

        {/* Empty state */}
        {sortedWorkItems.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              Inga aktiviteter registrerade
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityStreamCard;
